import React from "react";
import ChangesAccordion from "./ChangesAccordian";
import "./sidebar.scss";
import { Link } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import useDateTime from "../../hooks/useDateTime";
import ReactCountryFlag from "react-country-flag";
import { PayslipStatusMap } from "~/utils/fileUtils";
import { transformRoleDisplayName } from "~/utils/roleDisplayName";
import { parse } from "date-fns";
import { getTenantFieldDisplayName } from "./utils";

const StepCard = ({
  data,
  labels,
  nameLink,
  endType,
  overrideName = "",
  isEntityAccessAudit = false,
  objectType = "",
}) => {
  const { formatDateTime } = useDateTime();

  if (!data) {
    return (
      <div className="mb-5">
        <Skeleton width="8rem" className="mb-2"></Skeleton>
        <Skeleton width="10rem" className="mb-2"></Skeleton>
        <Skeleton className="mb-2"></Skeleton>
        <Skeleton width="10rem" className="mb-2"></Skeleton>
        <Skeleton height="4rem" className="mb-2"></Skeleton>
      </div>
    );
  }

  function handleAction(data) {
    if (endType === "PROCESS" || endType === "SHAREHOLDERS") {
      return handleActionProcess(data.type, data.action);
    } else {
      return handleLabelAction(data.action);
    }
  }

  const handleLabelAction = (action) => {
    const label = labels(data);
    switch (action) {
      case "create":
        return label.create;
      case "update":
        return label.update;
      case "delete":
        return label.delete;
      case "assign":
        return label.assign;
      case "remove":
        return label.remove;
      default:
        return "Invalid action";
    }
  };

  const handleActionProcess = (type, action) => {
    const actionMap = {
      PROCESS: {
        create: "Process Created",
        update: "Process Updated",
        delete: "Process Removed",
      },
      MILESTONE: {
        create: "Milestone Created",
        update: "Milestone Updated",
        delete: "Milestone Removed",
      },
      TASK: {
        create: "Task Created",
        update: "Task Updated",
        delete: "Task Removed",
        flagged: "Task Flagged",
        unflagged: "Task Unflagged",
      },
      CONVERSATION: {
        create: "Message Sent",
        delete: "Message Deleted",
      },
      CONVERSATION_DOCUMENT_DELETE: {
        delete: "Message Deleted (including attachments)",
      },
      DOCUMENT: {
        create: "File Uploaded",
        delete: "File(s) Deleted",
      },
      COMMENTS: {
        create: "Comment Added to a Task",
        delete: "Comment Deleted from a Task",
      },
      SHAREHOLDERS: {
        create: "Shareholder Added",
        update: "Information Updated",
        delete: "Shareholder Deleted",
      },
      PAYSLIPS: {
        create: "Payslip File Uploaded",
        delete: "Payslip/s Deleted",
        approve: "Payslips Matching Edited and submitted",
        submit_reject: "Payslips Matching Edited and submitted",
        reject: "Payslips Delivery Rejected",
        release: "Payslips Delivery Accepted",
      },
    };
    let result;
    if (type === "AD_HOC" || type === "ESCALATION") {
      result = action;
    } else {
      result = actionMap[type]?.[action] || "Invalid action";
    }
    return transformRoleDisplayName(result);
  };

  function formatDate(dateStr) {
    // If dateStr is in API format "2 Jan 2026 | 08:22:55", parse it first
    if (typeof dateStr === "string" && dateStr.includes(" | ")) {
      try {
        const [datePart, timePart] = dateStr.split(" | ");
        if (datePart && timePart) {
          // Combine: "2 Jan 2026 08:22:55"
          const dateTimeString = `${datePart} ${timePart}`;
          // Parse using date-fns for consistent cross-browser behavior
          const parsedDate = parse(
            dateTimeString,
            "d MMM yyyy HH:mm:ss",
            new Date(),
          );
          console.log("DEBUG formatDate:", {
            input: dateStr,
            dateTimeString,
            parsedDate: parsedDate.toString(),
            isValid: !Number.isNaN(parsedDate.getTime()),
          });
          if (!Number.isNaN(parsedDate.getTime())) {
            const result = formatDateTime({
              date: parsedDate,
              dateformat: "MMMM do, yyyy, h:mm a",
            });
            console.log("DEBUG formatDate result:", result);
            return result;
          }
        }
      } catch (error) {
        console.warn("Failed to parse API timestamp:", dateStr, error);
      }
      // Fallback: return raw string if parsing fails
      return dateStr;
    }
    // Otherwise, try to format it as a date
    try {
      return formatDateTime({
        date: dateStr,
        dateformat: "MMMM do, yyyy, h:mm a",
      });
    } catch (error) {
      // If formatting fails, return the original string
      console.warn("Failed to format date:", dateStr, error);
      return dateStr || "-";
    }
  }

  // Support both field naming conventions (name vs object_name)
  // Use overrideName if provided, otherwise fallback to data.name or data.object_name
  const rawDisplayName = overrideName || data.name || data.object_name || "";
  // Format the display name using tenant field mapping for human-friendly names
  const displayName = rawDisplayName
    ? getTenantFieldDisplayName(rawDisplayName)
    : "";
  const name = nameLink ? (
    <Link className="entity-link" to={nameLink(data)}>
      {transformRoleDisplayName(displayName)}
    </Link>
  ) : (
    transformRoleDisplayName(displayName)
  );

  const getShareholderTransactionLabel = (data) => {
    const shareholderName = data.name || data.object_name || "";
    let transactionType = null;
    const isShareholderRegistryContext =
      objectType === "shareholder" ||
      data.objectType === "shareholder" ||
      (data.shareholder && data.type !== "DIRECTOR_APPOINTMENT_AUDIT");

    if (isShareholderRegistryContext) {
      transactionType = data.type;
    }

    if (!transactionType) {
      transactionType =
        data.transaction_type ||
        data.share_transaction_type ||
        data.share_transactionType ||
        data.transactionType ||
        data.shareTransactionType;
    }

    if (!transactionType && data.old_data) {
      let oldDataObj = data.old_data;
      if (typeof data.old_data === "string") {
        try {
          oldDataObj = JSON.parse(data.old_data);
        } catch (e) {
          oldDataObj = null;
        }
      }

      if (
        oldDataObj &&
        typeof oldDataObj === "object" &&
        Object.keys(oldDataObj).length > 0
      ) {
        transactionType =
          oldDataObj.type ||
          oldDataObj.transaction_type ||
          oldDataObj.share_transaction_type ||
          oldDataObj.shareTransactionType;
      }
    }

    if (!transactionType && data.new_data) {
      let newDataObj = data.new_data;
      if (typeof data.new_data === "string") {
        try {
          newDataObj = JSON.parse(data.new_data);
        } catch (e) {
          newDataObj = null;
        }
      }

      if (
        newDataObj &&
        typeof newDataObj === "object" &&
        Object.keys(newDataObj).length > 0
      ) {
        transactionType =
          newDataObj.type ||
          newDataObj.transaction_type ||
          newDataObj.share_transaction_type ||
          newDataObj.shareTransactionType;
      }
    }

    if (shareholderName && transactionType) {
      return `Transaction ${shareholderName} ${transactionType} removed`;
    } else if (shareholderName) {
      return `Transaction ${shareholderName} removed`;
    }
    return null;
  };

  const handleNaming = () => {
    if (data.type === "DIRECTOR_APPOINTMENT_AUDIT") return null;

    if (
      data.action === "delete" &&
      (data.name || data.object_name) &&
      (objectType === "shareholder" ||
        data.objectType === "shareholder" ||
        data.shareholder ||
        data.type === "SHAREHOLDER_REGISTRY" ||
        data.transaction_type ||
        data.share_transaction_type ||
        data.share_transactionType ||
        data.transactionType ||
        (data.old_data &&
          (data.old_data.type || data.old_data.transaction_type)) ||
        (data.new_data &&
          (data.new_data.type || data.new_data.transaction_type)))
    ) {
      const transactionLabel = getShareholderTransactionLabel(data);
      if (transactionLabel) {
        return transactionLabel;
      }
    }

    if (endType === "USERACCESS") {
      return (
        <>
          <ReactCountryFlag
            countryCode={data?.country || ""}
            svg
            className=""
          />
          {` ${transformRoleDisplayName(displayName)} (${data?.country})`}
        </>
      );
    } else if (data?.attachment) {
      // GXM-2023: Only show attachment filename, not the person/entity name
      return <span className="audit-name-space">{data?.attachment}</span>;
    } else {
      return name;
    }
  };
  // Support both field naming conventions (actor_name vs updated_by_name)
  const actorName = data.actor_name || data.updated_by_name || "";
  const actorEmail = data.actor_email || data.updated_by_email || "";
  // Support multiple timestamp field names from different API responses
  const timestamp =
    data.timestamp || data.updatedAt || data.updated_at || data.createdAt;

  const getAppointmentLabel = (data) => {
    switch (data.action) {
      case "create":
        return `Appointment ${data.name} in ${data.position} added`;
      case "update":
        return `Appointment ${data.name} in ${data.position} updated`;
      case "delete":
        return `Appointment ${data.name} in ${data.position} removed`;
      default:
        return "";
    }
  };

  return (
    <div className="step">
      {(() => {
        const actionText = handleAction(data);
        if (actionText.includes("\n")) {
          const [mainText, subtitle] = actionText.split("\n");
          return (
            <>
              <h5 className="title m-0 mb-1">{mainText}</h5>
              {subtitle && <p className="desc-text m-0 mb-1">{subtitle}</p>}
            </>
          );
        }
        return <h5 className="title m-0 mb-1">{actionText}</h5>;
      })()}
      <p className="desc-text m-0 mb-1">{handleNaming()}</p>
      {/* only in case of Managed Entities */}
      {["assign", "remove"].includes(data.action) && (
        <p className="desc-text m-0 mb-1">{data.entity_name}</p>
      )}
      {/* only in case of payslips */}
      {data?.type === "PAYSLIPS" &&
        ["approve", "reject", "release", "delete", "submit_reject"].includes(
          data.action,
        ) && (
          <>
            <p className="desc-text m-0 mb-1">{`${data?.employee_name || ""}`}</p>
            <p className="desc-text m-0 mb-1">{`${PayslipStatusMap[data?.match_status] || ""}`}</p>
          </>
        )}
      {data.type === "DIRECTOR_APPOINTMENT_AUDIT" && (
        <>
          <p className="desc-text m-0 mb-1">{getAppointmentLabel(data)}</p>
          {data.action !== "delete" && (
            <p className="desc-text m-0 mb-1">{data.name}</p>
          )}
        </>
      )}
      <p className="desc-text m-0 mb-1">
        {actorName} {actorEmail ? `(${actorEmail})` : ""}
      </p>
      <p className="date-time m-0">{formatDate(timestamp)}</p>
      {/* Only show Compare Changes for update actions (hide for create/delete) */}
      {data.action === "update" &&
      ((data.new_data && Object.keys(data.new_data).length > 0) ||
        (data.old_data && Object.keys(data.old_data).length > 0)) ? (
        <ChangesAccordion
          old_data={data.old_data || {}}
          new_data={data.new_data || {}}
          isEntityAccessAudit={isEntityAccessAudit}
        />
      ) : null}
    </div>
  );
};

export default StepCard;
